"use client";

import { createCalendar, parseDate } from "@internationalized/date";
import { type DateValue, useDateField } from "@react-aria/datepicker";
import { useObjectRef } from "@react-aria/utils";
import { type DateFieldStateOptions, useDateFieldState } from "@react-stately/datepicker";
import type { VariantProps } from "class-variance-authority";
import { type ChangeEvent, type ChangeEventHandler, type ReactNode, type Ref, useEffect } from "react";

import { cn } from "../../../utils";
import { useTextFieldRootContext } from "../context";
import { inputContainerStyles } from "../style";
import DateSegment from "./DateSegment";
import { dateSegmentContainerStyles } from "./style";

export type TextFieldInputDateProps = Omit<
  DateFieldStateOptions<DateValue>,
  "value" | "defaultValue" | "onChange" | "locale" | "isDisabled" | "createCalendar"
> &
  VariantProps<typeof inputContainerStyles> &
  VariantProps<typeof dateSegmentContainerStyles> & {
    className?: string;
    containerClassName?: string;
    name?: string;
    value?: string;
    onChange?: ChangeEventHandler<HTMLInputElement>;
    disabled?: boolean;
    prefix?: ReactNode;
    suffix?: ReactNode;
    ref?: Ref<HTMLInputElement>;
  };

const parseDateValue = (value?: string) => {
  if (!value) {
    return undefined;
  }
  try {
    return parseDate(value);
  } catch {
    console.error(`Failed to parse date value: ${value}`);
    return undefined;
  }
};

const InputDate = ({
  className,
  containerClassName,
  variant = "outline",
  color = "primary",
  value,
  onChange,
  disabled,
  prefix,
  suffix,
  ref: forwardedRef,
  ...props
}: TextFieldInputDateProps) => {
  const ref = useObjectRef(forwardedRef);
  const { setTextField } = useTextFieldRootContext<"input">();

  const state = useDateFieldState({
    ...props,
    value: parseDateValue(value),
    onChange: (value: DateValue | null) =>
      onChange &&
      onChange({
        target: { value: value?.toString() ?? undefined, name: props.name },
      } as ChangeEvent<HTMLInputElement>),
    isDisabled: disabled,
    label: true,
    locale: "en-UK",
    createCalendar,
  });

  const textField = useDateField(props, state, ref);
  const { fieldProps, isInvalid } = textField;

  useEffect(() => {
    setTextField(textField);
  }, [isInvalid]);

  useEffect(() => {
    // reset value
    if (!value || !parseDateValue(value)) {
      state.setValue(null);
    }
  }, [value]);

  return (
    <div
      {...fieldProps}
      ref={ref}
      data-invalid={isInvalid}
      data-disabled={disabled}
      // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role
      role="button"
      tabIndex={-1}
      className={cn(inputContainerStyles({ variant, color }), "outline-none", containerClassName)}
    >
      {prefix}

      <span className={cn(dateSegmentContainerStyles({ variant }), className, "-ml-0.5 whitespace-nowrap")}>
        {state.segments
          .filter(segment => segment.text)
          .map((segment, index) => (
            <DateSegment key={index} segment={segment} state={state} />
          ))}
      </span>

      {suffix}
    </div>
  );
};

export default InputDate;
